import { injectable, inject } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@users/json-rpc-dispatcher';
import DITypes from '@users/json-rpc-dispatcher/DITypes';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import { UserId } from '@users/user/vo/User';

@injectable()
class TwoFactorRepository {
  constructor(@inject(DITypes.JsonRpcDispatcher) private readonly rpc: IDispatcher) {}

  /**
   * generate token.
   * token is needed while requesting enable two factor auth by phone and turning off two factor verification by phone
   */
  async generateTwoFactorToken(password: string) {
    const request = new Request('user.generate_update_token', { password });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * generate and send two factor verification code
   */
  async sendTwoFactorCode(deliveryMethod: string = '') {
    const request = new Request('two_factor.send_code', { deliveryMethod });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * turn off two factor authorization by text massages.
   */
  async disableMobileTwoFactor(updateToken: string) {
    const request = new Request('user.mobile_two_factor.disable', { updateToken });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * check two factor verification code and enable two factor authorization by text massages.
   */
  async enableMobileTwoFactor(code: string) {
    const request = new Request('user.mobile_two_factor.enable', { code });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * check two factor verification code
   */
  async checkTwoFactorCode(code: string) {
    const request = new Request('two_factor.attempt', { code });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * adding mobile phone to receive two factor codes by text messages
   */
  async addTwoFactorPhone(mobilePhone: string, updateToken: string) {
    const request = new Request('user.mobile_phone.add', { mobilePhone, updateToken });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * generate two factor code for user by support
   * !!!NOTE: this method won't send code to user, just write code into database and return it to FE!!!
   */
  generateTwoFactorCode = async (id: UserId) => {
    const request = new Request('two_factor.generate_code', { id });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };
}

export default TwoFactorRepository;
